/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, Dec 2021
 * @flow
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../../components/Button';
import constants from '../../../../globalization';
import Checkbox from '../../../../components/Checkbox';
import backImg from '../../../../assets/images/Fireboard/back2_red.png';
import FireboardUtils from '../../../../util/fireboard-util';
import { storeProductionEnum } from '../../../../enums';
import classes from './EditProductionRate.module.css';

const Weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const EditProductionRate = ({
  productionRate,
  onClose,
  onEditProductionRate,
  productionOverrideReasons,
  actionType,
}) => {
  const [overrides, setOverrides] = useState({
    prepWindow: '',
    production: '',
    comment: '',
  });
  const [reason, setReason] = useState(0);
  const { dayOfWeek, startTime, endTime, prepWindow, production, includeOnPremise } = productionRate || {};

  useEffect(() => {
    const { overridePrepWindow, overrideProduction } = productionRate || {};
    const prepWindow = overridePrepWindow === null ? '' : String(overridePrepWindow);
    const production = overrideProduction === null ? '' : String(overrideProduction);
    setOverrides({ prepWindow, production });
  }, []);

  const onInputChange = key => event => {
    const value = event?.target?.value;
    setOverrides(prevState => {
      return {
        ...prevState,
        [key]: value,
      }
    });
  };

  const onChangeOverrideReason = event => {
    const reasonId = event.target.value;
    setReason(+reasonId);
  };

  const onSave = () => {
    onEditProductionRate && onEditProductionRate({
      overridePrepWindow: overrides.prepWindow,
      overrideProduction: overrides.production,
      productionOverrideReason: reason,
      comment: overrides.comment,
    }, actionType);
  };

  const isSaveDisabled = () => {
    let isReasonInvalid = false;
    if (String(reason).match(/[^$,.\d]/)) {
      isReasonInvalid = true;
    }
    //comment is required if reason is other
    let isCommentInvalid = false;
    const trimmedComment = overrides.comment ? overrides.comment.trim() : '';
    if (reason === 0 && trimmedComment === '') {
      isCommentInvalid = true;
    }
    return (overrides.prepWindow.length === 0 || overrides.production.length === 0 || isReasonInvalid || isCommentInvalid);
  };

  const isVisibleInfo = () => {
    return actionType !== storeProductionEnum.BULKEDIT;
  };

  return (
    <div className={classes.container}>
      <div className={classes.headingWrapper}>
        <div className={classes.backButton} onClick={onClose}>
          <img src={backImg} className={classes.backImage} alt="Back" />
        </div>
        <span className={classes.title}>{constants.overrideProductionRate}</span>
      </div>
      <div className={classes.nonEditableWrapper}>
        <div className={classes.row}>
          <Input
            label={constants.dayOfWeek}
            type={'text'}
            value={Weekdays[dayOfWeek]}
            onChange={null}
            disabled={true}
          />
        </div>
        {isVisibleInfo() && (
          <React.Fragment>
            <div className={classes.row}>
              <Input
                label={constants.startTime}
                type={'text'}
                value={FireboardUtils.getTimeIn12HourClock(startTime)}
                onChange={null}
                disabled={true}
              />
              <Input
                label={constants.endTime}
                type={'text'}
                value={FireboardUtils.getTimeIn12HourClock(endTime)}
                onChange={null}
                disabled={true}
              />
            </div>
            <div className={classes.row} style={{ justifyContent: 'flex-start' }}>
              <Checkbox readOnly checked={includeOnPremise} />
              <div className={classes.label}>{constants.includeOnPremise}</div>
            </div>
            <div className={classes.row}>
              <Input
                label={constants.prepWindow}
                type={'text'}
                value={prepWindow || ''}
                onChange={null}
                disabled={true}
              />
              <Input
                label={constants.production}
                type={'text'}
                value={production || ''}
                onChange={null}
                disabled={true}
              />
            </div>
          </React.Fragment>
        )}
      </div>
      <div>
        <div className={classes.row}>
          <Input
            label={constants.todaysPrepOverride}
            type={'number'}
            value={overrides.prepWindow}
            onChange={onInputChange('prepWindow')}
          />
          <Input
            label={constants.todaysProductionOverride}
            type={'number'}
            value={overrides.production}
            onChange={onInputChange('production')}
          />
        </div>
        <div className={classes.row}>
          <div className={classes.textAreaWrapper}>
            <span className={classes.label}>{constants.overrideReason}</span>
            <select value={reason} onChange={onChangeOverrideReason} className={classes.dropdown}>
              {productionOverrideReasons.map(reason =>
                <option
                  key={`reasonId-${reason.id}`}
                  value={reason.id}
                  selected={reason === reason.id}>
                  {reason.name}
                </option>
              )}
            </select>
          </div>
        </div>
        <div className={classes.row}>
          <TextArea
            label={constants.comment}
            type={'text'}
            value={overrides.comment}
            onChange={onInputChange('comment')}
          />
        </div>
      </div>
      <Button className={classes.button} onClick={onSave} disabled={isSaveDisabled()}>
        <span className={classes.buttonText}>{constants.saveLabel}</span>
      </Button>
    </div >
  );
};

export default EditProductionRate;

EditProductionRate.propTypes = {
  productionRate: PropTypes.object,
  onEditProductionRate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  productionOverrideReasons: PropTypes.array,
};

EditProductionRate.defaultProps = {
  productionRate: {},
  onEditProductionRate: () => { },
  onClose: () => { },
  productionOverrideReasons: [],
};

const Input = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  disabled,
}) => {
  return (
    <div className={classes.inputWrapper}>
      <span className={classes.label}>{label}</span>
      <input
        className={classes.input}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
};

const TextArea = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  disabled,
}) => {
  return (
    <div className={classes.textAreaWrapper}>
      <span className={classes.label}>{label}</span>
      <textarea
        className={classes.textArea}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
};